<template>
  <section class="training-videos" aria-label="Schulungsvideos">
    <header class="video-heading">
      <div>
        <span class="video-eyebrow">Mitarbeiter-Weiterbildung</span>
        <h2>Schulungsvideos</h2>
        <p>Wissen auffrischen und Kursinhalte in deinem Tempo vertiefen.</p>
      </div>
      <v-btn v-if="canManage" outlined color="#58d388" @click="openEditor()">
        <v-icon left>mdi-video-plus-outline</v-icon>
        Video hinzufügen
      </v-btn>
    </header>

    <v-autocomplete
      v-if="canManage && studioOptions.length"
      v-model="studioFilter"
      :items="studioOptions"
      item-text="name"
      item-value="_id"
      dark
      color="#58d388"
      :menu-props="{ dark: true }"
      outlined
      clearable
      hide-details
      label="Nach Studio filtern"
      placeholder="Alle Studios"
      persistent-placeholder
      class="video-filter" />
    <v-alert v-if="notice" type="success" text dismissible @input="notice = ''">{{ notice }}</v-alert>
    <v-alert v-if="error" type="error" text>
      {{ error }}
      <v-btn text @click="refresh">Erneut laden</v-btn>
    </v-alert>
    <div v-if="loading" class="video-empty">
      <v-progress-circular indeterminate color="#58d388" />
      <span>Videos werden geladen ...</span>
    </div>
    <div v-else-if="!error && !visibleVideos.length" class="video-empty">
      <v-icon color="#58d388">mdi-video-outline</v-icon>
      <p>
        {{
          studioFilter
            ? "Für dieses Studio sind noch keine Schulungsvideos verfügbar."
            : "Hier erscheinen die Schulungsvideos, sobald sie für dein Studio verfügbar sind."
        }}
      </p>
    </div>
    <div v-else-if="!error" class="video-grid">
      <article v-for="video in visibleVideos" :key="video._id" class="video-card">
        <div class="video-card-heading">
          <v-icon color="#58d388">mdi-play-circle-outline</v-icon>
          <h3>{{ video.title }}</h3>
        </div>
        <p v-if="video.description" class="video-summary">{{ video.description }}</p>
        <div class="video-studios" aria-label="Sichtbar in diesen Studios">
          <span v-if="!video.studios.length">Alle Studios</span>
          <span v-for="studio in video.studios" :key="studio._id">{{ studio.name }}</span>
        </div>
        <p v-if="!video.media" class="video-draft">Upload ausstehend · Nur für die Verwaltung sichtbar</p>
        <div class="video-actions">
          <v-btn v-if="video.media" text color="#58d388" @click="openPlayer(video)">
            <v-icon left>mdi-play</v-icon>
            Video ansehen
          </v-btn>
          <div v-if="canManage" class="video-manage">
            <v-btn dark icon :aria-label="`${video.title} bearbeiten`" @click="openEditor(video)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn
              dark
              icon
              :aria-label="`${video.title} löschen`"
              @click="confirmDelete(video)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </article>
    </div>

    <v-dialog v-model="editorOpen" :persistent="saving" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="760">
      <v-card dark class="video-dialog">
        <header class="video-dialog-heading">
          <h2>{{ form.id ? "Schulungsvideo bearbeiten" : "Schulungsvideo hinzufügen" }}</h2>
          <v-btn icon :disabled="saving" aria-label="Schliessen" @click="editorOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text>
          <v-alert v-if="formError" type="error" text>{{ formError }}</v-alert>
          <v-text-field v-model="form.title" dark color="#58d388" label="Titel" outlined maxlength="160" counter="160" :disabled="saving" />
          <v-textarea
            v-model="form.description"
            dark
            color="#58d388"
            label="Beschreibung (optional)"
            outlined
            rows="3"
            auto-grow
            maxlength="5000"
            counter="5000"
            :disabled="saving" />
          <v-autocomplete
            :value="form.studios"
            :items="editorStudios"
            item-text="name"
            item-value="_id"
            dark
            color="#58d388"
            :menu-props="{ dark: true }"
            label="Studios"
            placeholder="Alle Studios"
            persistent-placeholder
            multiple
            chips
            deletable-chips
            clearable
            outlined
            :loading="studiosLoading"
            :disabled="saving || studiosLoading || !!studioError"
            hint="Ohne Auswahl ist das Video in allen Studios sichtbar. Die Auswahl kannst du später ändern."
            persistent-hint
            @input="form.studios = $event || []" />
          <v-alert v-if="studioError" type="warning" text class="mt-4">
            {{ studioError }}
            <v-btn text @click="loadStudios">Erneut laden</v-btn>
          </v-alert>
          <p v-if="form.media" class="video-file">Aktuelles Video: {{ form.media.originalName }}</p>
          <v-file-input
            v-model="file"
            dark
            color="#58d388"
            :label="form.media ? 'Video ersetzen (optional)' : 'Video auswählen'"
            accept="video/*,.mp4,.m4v,.mov,.webm,.avi,.3gp"
            outlined
            show-size
            :disabled="saving"
            hint="Maximal 15 GB. Nach dem Upload wird das Video kurz verarbeitet."
            persistent-hint
            class="mt-6" />
          <div v-if="saving" class="video-progress" role="status" aria-live="polite">
            <span>{{ saveLabel }}</span>
            <v-progress-linear :value="progress" :indeterminate="phase !== 'upload'" color="#58d388" rounded height="6" />
            <span class="video-secondary">Bitte lasse diese Seite während des Uploads geöffnet.</span>
          </div>
        </v-card-text>
        <v-card-actions class="video-dialog-actions">
          <v-btn text :disabled="saving" @click="editorOpen = false">Abbrechen</v-btn>
          <v-btn
            color="#58d388"
            class="black--text"
            :loading="saving"
            :disabled="saving || conflict || studiosLoading || !!studioError"
            @click="save">
            {{ file ? "Hochladen & speichern" : "Speichern" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="playerOpen" max-width="1040" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="playerOpen && selectedVideo" dark class="video-dialog">
        <header class="video-dialog-heading">
          <h2>{{ selectedVideo.title }}</h2>
          <v-btn icon aria-label="Video schliessen" @click="playerOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text>
          <SecuredMedia
            :key="playerKey"
            :src="playbackPath"
            media-type="video"
            stream-playback
            :cache-key="playerKey"
            :show-app-download-action="false"
            eager
            @error="playbackError = true" />
          <v-alert v-if="playbackError" type="error" text class="mt-4">
            Das Video konnte nicht geladen werden. Bitte versuche es erneut.
            <v-btn
              text
              @click="retryPlayback">
              Erneut laden
            </v-btn>
          </v-alert>
          <p v-if="selectedVideo.description" class="video-description">{{ selectedVideo.description }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!deleteTarget" :persistent="deleting" max-width="520" @input="!$event && !deleting && (deleteTarget = null)">
      <v-card v-if="deleteTarget" dark class="video-dialog">
        <header class="video-dialog-heading"><h2>Video löschen?</h2></header>
        <v-card-text>
          <p>«{{ deleteTarget.title }}» wird aus den Schulungsvideos entfernt und das Video wird gelöscht.</p>
          <v-alert v-if="deleteError" type="error" text>{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions class="video-dialog-actions">
          <v-btn text :disabled="deleting" @click="deleteTarget = null">Abbrechen</v-btn>
          <v-btn color="error" :loading="deleting" @click="removeVideo">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import SecuredMedia from "@/views/akademie/SecuredMedia.vue"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"

const MAX_VIDEO_SIZE = 15 * 1024 * 1024 * 1024
const emptyForm = () => ({ id: null, title: "", description: "", studios: [], media: null, revision: 0 })
const errorMessage = (error, fallback) => (error.response && error.response.data && error.response.data.error) || error.message || fallback

export default {
  name: "EmployeeCourseVideos",
  components: { SecuredMedia },
  data() {
    return {
      videos: [],
      studios: [],
      studioFilter: null,
      loading: false,
      error: "",
      notice: "",
      loadId: 0,
      studiosLoading: false,
      studioError: "",
      editorOpen: false,
      form: emptyForm(),
      formError: "",
      file: null,
      saving: false,
      phase: "",
      progress: 0,
      conflict: false,
      uploadController: null,
      disposed: false,
      playerOpen: false,
      selectedVideo: null,
      playbackError: false,
      playerAttempt: 0,
      deleteTarget: null,
      deleting: false,
      deleteError: "",
    }
  },
  computed: {
    canManage() {
      return userService.hasRight("gf_manageEmployeeCourses")
    },
    activeStudioId() {
      return this.$store.getters["studioStore/getSelectedStudioId"]
    },
    visibleVideos() {
      return this.videos.filter(
        (video) => !this.studioFilter || !video.studios.length || video.studios.some((studio) => studio._id === this.studioFilter),
      )
    },
    studioOptions() {
      const studios = new Map(this.studios.map((studio) => [studio._id, studio]))
      this.videos.forEach((video) => video.studios.forEach((studio) => studios.set(studio._id, studio)))
      return [...studios.values()].sort((a, b) => a.name.localeCompare(b.name, "de"))
    },
    editorStudios() {
      const studios = new Map(this.studioOptions.map((studio) => [studio._id, studio]))
      this.form.studios.forEach((id) => {
        if (!studios.has(id)) studios.set(id, { _id: id, name: "Studio nicht verfügbar" })
      })
      return [...studios.values()]
    },
    playbackPath() {
      return this.selectedVideo ? api.employeeCourses.videos.getPlaybackPath(this.selectedVideo._id) : ""
    },
    playerKey() {
      return this.selectedVideo ? `${this.selectedVideo._id}-${this.selectedVideo.updatedAt}-${this.playerAttempt}` : ""
    },
    saveLabel() {
      return this.phase === "upload"
        ? `Video wird hochgeladen: ${this.progress} %`
        : this.phase === "prepare"
        ? "Upload wird vorbereitet ..."
        : "Video wird gespeichert ..."
    },
  },
  watch: {
    activeStudioId() {
      this.playerOpen = false
      this.selectedVideo = null
      this.videos = []
      this.refresh()
    },
  },
  created() {
    this.refresh()
    if (this.canManage) this.loadStudios()
  },
  beforeDestroy() {
    this.disposed = true
    this.loadId += 1
    if (this.uploadController) this.uploadController.abort()
  },
  methods: {
    async refresh() {
      const loadId = ++this.loadId
      this.loading = true
      this.error = ""
      try {
        const videos = await api.employeeCourses.videos.fetchVideos()
        if (loadId === this.loadId) {
          this.videos = videos || []
          if (this.selectedVideo) {
            this.selectedVideo = this.videos.find((video) => video._id === this.selectedVideo._id) || null
            if (!this.selectedVideo || !this.selectedVideo.media) this.playerOpen = false
          }
        }
      } catch (error) {
        if (loadId === this.loadId) this.error = errorMessage(error, "Videos konnten nicht geladen werden.")
      } finally {
        if (loadId === this.loadId) this.loading = false
      }
    },
    async loadStudios() {
      this.studiosLoading = true
      this.studioError = ""
      try {
        this.studios = (await api.fetchStudios()).filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
      } catch (error) {
        this.studioError = "Studios konnten nicht geladen werden. Bitte versuche es erneut."
      } finally {
        this.studiosLoading = false
      }
    },
    confirmDelete(video) {
      this.deleteTarget = video
      this.deleteError = ""
    },
    retryPlayback() {
      this.playerAttempt += 1
      this.playbackError = false
    },
    openPlayer(video) {
      this.selectedVideo = video
      this.playbackError = false
      this.playerAttempt = 0
      this.playerOpen = true
    },
    openEditor(video = null) {
      this.form = video
        ? {
            id: video._id,
            title: video.title,
            description: video.description,
            studios: video.studios.map((studio) => studio._id),
            media: video.media,
            revision: video.revision,
          }
        : emptyForm()
      this.file = null
      this.formError = ""
      this.conflict = false
      this.progress = 0
      this.editorOpen = true
    },
    async save() {
      if (this.saving || this.conflict || !this.canManage || this.studiosLoading || this.studioError) return
      this.formError = ""
      if (!this.form.title.trim()) {
        this.formError = "Bitte einen Titel eingeben."
        return
      }
      if (!this.file && !this.form.media) {
        this.formError = "Bitte ein Video auswählen."
        return
      }
      if (this.file && (!this.file.size || this.file.size > MAX_VIDEO_SIZE)) {
        this.formError = "Das Video muss eine gültige Datei sein und darf maximal 15 GB gross sein."
        return
      }
      if (this.file && !(String(this.file.type || "").startsWith("video/") || /\.(mp4|m4v|mov|webm|avi|3gp)$/i.test(this.file.name))) {
        this.formError = "Bitte eine Videodatei auswählen."
        return
      }
      this.saving = true
      this.notice = ""
      this.phase = "prepare"
      this.progress = 0
      this.uploadController = new AbortController()
      let session = null
      const metadata = { title: this.form.title.trim(), description: this.form.description.trim(), studios: [...this.form.studios] }
      try {
        if (!this.form.id) {
          const draft = await api.employeeCourses.videos.createVideo(metadata)
          this.form.id = draft._id
          this.form.revision = draft.revision
        }
        if (this.disposed) throw new Error("Upload abgebrochen.")
        if (this.file) {
          session = await api.employeeCourses.videos.prepareUpload(this.form.id, this.file)
          this.phase = "upload"
          await uploadCloudflareMedia(this.file, session, {
            signal: this.uploadController.signal,
            onProgress: (loaded, total) => {
              this.progress = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0
            },
          })
        }
        if (this.disposed) throw new Error("Upload abgebrochen.")
        this.phase = "save"
        await api.employeeCourses.videos.updateVideo(this.form.id, {
          ...metadata,
          revision: this.form.revision,
          ...(session ? { media: session } : {}),
        })
        this.editorOpen = false
        this.notice = "Schulungsvideo gespeichert."
      } catch (error) {
        this.formError = errorMessage(error, "Das Video konnte nicht gespeichert werden.")
        this.conflict = Boolean(error.response && error.response.status === 409)
        if (session) {
          try {
            const result = await api.employeeCourses.videos.cancelUpload(this.form.id, session)
            if (result.retained) {
              this.editorOpen = false
              this.notice = "Schulungsvideo gespeichert."
            } else {
              this.form.revision = result.revision
            }
          } catch (_) {
            this.conflict = true
            this.formError += " Bitte schliesse den Dialog und prüfe den Eintrag vor einem erneuten Versuch."
          }
        }
        if (this.conflict) this.formError += " Bitte schliesse den Dialog und öffne den Eintrag erneut."
      } finally {
        this.saving = false
        this.uploadController = null
        if (!this.disposed) await this.refresh()
      }
    },
    async removeVideo() {
      if (this.deleting || !this.deleteTarget) return
      this.deleting = true
      this.deleteError = ""
      try {
        await api.employeeCourses.videos.deleteVideo(this.deleteTarget._id, this.deleteTarget.revision)
        if (this.selectedVideo && this.selectedVideo._id === this.deleteTarget._id) this.playerOpen = false
        this.deleteTarget = null
        this.notice = "Schulungsvideo gelöscht."
        await this.refresh()
      } catch (error) {
        this.deleteError = errorMessage(error, "Das Video konnte nicht gelöscht werden.")
        await this.refresh()
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.training-videos {
  margin: 2rem 0;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.25rem;
  background: #171c19;
  color: #f4f7f5;
  font-size: 1rem;
}
.video-heading,
.video-dialog-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.video-heading {
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}
.video-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 600;
}
.video-heading h2,
.video-dialog h2 {
  font-size: 1.5rem;
  line-height: 1.3;
  margin: 0.25rem 0 0.5rem;
  overflow-wrap: anywhere;
}
.video-heading p {
  margin: 0;
  color: #bdc9c1;
  font-size: 1rem;
}
.video-filter {
  max-width: 28rem;
  margin-bottom: 1.5rem;
}
.video-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.video-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  background: #202823;
}
.video-card-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.video-card h3 {
  font-size: 1.125rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.video-summary {
  margin: 1rem 0;
  font-size: 1rem;
  color: #d0d9d3;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.video-studios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin: 1rem 0;
}
.video-studios span {
  font-size: 0.75rem;
  line-height: 1.5;
  padding: 0.25rem 0.625rem;
  border-radius: 0.5rem;
  background: rgba(88, 211, 136, 0.1);
  color: #b2e9c5;
  overflow-wrap: anywhere;
}
.video-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: auto;
}
.video-manage {
  display: flex;
  margin-left: auto;
}
.video-draft,
.video-secondary,
.video-file {
  font-size: 0.875rem;
  color: #c8d4cc;
  overflow-wrap: anywhere;
}
.video-file {
  margin-top: 1.5rem;
}
.video-empty {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 0;
  font-size: 1rem;
  color: #c8d4cc;
}
.video-empty p {
  margin: 0;
}
.video-dialog {
  background: #1c231f !important;
  font-size: 1rem;
}
.video-dialog-heading {
  padding: 1.5rem;
}
.video-dialog .v-card__text {
  font-size: 1rem;
}
.video-dialog-actions {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 1.5rem 1.5rem;
}
.video-description {
  margin: 1.5rem 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 1rem;
  line-height: 1.6;
}
.video-progress {
  display: grid;
  gap: 0.75rem;
  margin-top: 1.5rem;
  font-size: 0.875rem;
}
.video-dialog ::v-deep .v-messages,
.video-dialog ::v-deep .v-counter,
.video-dialog ::v-deep .v-chip {
  font-size: 0.75rem;
}
.video-dialog ::v-deep .v-label,
.video-dialog ::v-deep input,
.video-dialog ::v-deep textarea {
  font-size: 1rem;
}
.video-card .v-btn,
.video-heading .v-btn,
.video-dialog .v-btn {
  font-size: 0.875rem;
  letter-spacing: normal;
}
@media (max-width: 700px) {
  .training-videos {
    padding: 1rem;
  }
  .video-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .video-dialog-heading {
    padding: 1rem;
  }
  .video-dialog-actions {
    padding: 1rem;
  }
}
</style>
